/**
 * The AI Service detail page's Config tab (task 380).
 *
 * Embeds the SAME collaboration-config editor surface Advanced uses
 * (`ConfigForm`, the component ConfigDetailPage's Definition tab renders),
 * scoped to this service's `endpoint.collaborationConfigId` — no bespoke
 * editor.
 *
 * **Five views, one at a time** (task 803, superseding the stacked
 * presentation of task 802; a fourth added by task 973 and a fifth by task
 * 1003). A segmented row picks between them:
 *
 *   1. **Graph Config** (default) — the definition summary (node/edge counts),
 *      the server-side Validate action (POST /collaboration-configs/validate)
 *      and its error list, the link out to the unscoped Advanced page, the
 *      name/description, and the graph editor itself.
 *   2. **Input / Output / Mapping** — the schema builders and output mapping.
 *   3. **Agents** — the task 796 dossier, unchanged.
 *   4. **Access** — who may call this AI Service (task 973): the shared
 *      `ServiceAccessSection`, the same editor the Composer mounts. It edits
 *      the ENDPOINT's auth policy rather than the collaboration config, so it
 *      renders only when the host passes `endpoint`, and it renders before the
 *      config guards — a service with no config attached can still say who may
 *      call it. `?tab=config&section=access` deep-links straight to it.
 *
 *   5. **Schedules** — when this AI Service runs by itself (task 1003): the
 *      `ServiceSchedulesSection`. Another own-save surface bound to the
 *      ENDPOINT rather than the collaboration config, appended after Access and
 *      gated on `innovate.fe.schedules.manage` as well as on the host passing
 *      `endpoint`. `?tab=config&section=schedules` deep-links to it.
 *
 * Views 1 and 2 are ONE `ConfigForm` with one save row that saves both; the
 * hidden one is hidden, never unmounted, so a schema edit survives a switch to
 * the graph view — or a detour through Agents — and still reaches the update
 * call. Agents is read-mostly and carries no save row, so the whole form hides
 * while it shows.
 *
 * **Agents live here at all** because of task 800: the definition and the
 * agents it names are both answers to "how is this built". `?tab=agents` still
 * resolves, as a page-level alias onto this tab — and arrives with the Agents
 * segment active, via `initialSection`, so the old deep link still shows
 * agents rather than dropping the reader on the graph.
 *
 * **The tab is a VIEW first** (task 806). It opens read-only — name and
 * description as text, the schemas and mapping as read-only renderings, the
 * definition as JSON — with Edit in the panel's top-right. Editing turns the
 * same regions back into the editors already described above, and Save appears
 * beside Cancel the moment anything changes. The mode spans the two form
 * segments, because they are one record and one save; the Agents segment is a
 * reading surface of its own and is untouched by it.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { ConfigForm, type ConfigFormViewState } from '@/components/configs/ConfigForm';
import { ConfigModeControls } from '@/components/configs/ConfigModeControls';
import {
  useCollaborationConfig,
  useUpdateCollaborationConfig,
} from '@/hooks/useCollaborationConfigs';
import {
  collaborationConfigValidationApi,
  type DefinitionValidationResponse,
} from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import type { Endpoint } from '@/types';
import { useFlag } from '@/hooks/useFeatureFlags';
import { ServiceAgentsTab } from './ServiceAgentsTab';
import { ServiceAccessSection } from './ServiceAccessSection';
import { ServiceSchedulesSection } from './ServiceSchedulesSection';

/**
 * The views the section rail picks between.
 *
 * `access` joined them in task 973. It is the odd one out on purpose: the other
 * three edit the collaboration config, and Access edits the AI Service's own
 * auth policy — but "who can call this service" is a configuration question,
 * and the Config tab is where a reader looks for one. It renders only when the
 * page hands the tab its endpoint, which is what the policy lives on.
 */
export const CONFIG_VIEWS = ['graph', 'schemas', 'agents', 'access', 'schedules'] as const;
export type ConfigView = (typeof CONFIG_VIEWS)[number];

/** Whether a raw `?section=` value names a view of this tab. */
export function isConfigView(value: string | null | undefined): value is ConfigView {
  return !!value && (CONFIG_VIEWS as readonly string[]).includes(value);
}

const VIEWS: Array<{ id: ConfigView; label: string; testId: string }> = [
  { id: 'graph', label: 'Graph Config', testId: 'service-config-subnav-graph' },
  { id: 'schemas', label: 'Input / Output / Mapping', testId: 'service-config-subnav-schemas' },
  { id: 'agents', label: 'Agents', testId: 'service-config-subnav-agents' },
];

/** Appended only when the tab was given the endpoint the policy lives on. */
const ACCESS_VIEW: { id: ConfigView; label: string; testId: string } = {
  id: 'access',
  label: 'Access',
  testId: 'service-config-subnav-access',
};

/**
 * Schedules (task 1003), appended after Access and gated the same way, plus its
 * own flag. Like Access it edits the AI SERVICE rather than the collaboration
 * config — a schedule is a trigger bound to the service, not part of the
 * definition — so it is an own-save view with no Edit / Save / Cancel row.
 */
const SCHEDULES_VIEW: { id: ConfigView; label: string; testId: string } = {
  id: 'schedules',
  label: 'Schedules',
  testId: 'service-config-subnav-schedules',
};

interface ServiceConfigTabProps {
  collaborationConfigId: string | null;
  /** Passed through to the agents dossier for its run-history links. */
  serviceId?: string;
  /**
   * The AI Service itself, when the host has it. Required for the Access view
   * (task 973) — the auth policy is a property of the endpoint, not of the
   * collaboration config — and its absence is what keeps the rail at three
   * entries for any host that mounts this tab without one.
   */
  endpoint?: Endpoint;
  /**
   * Which view to open on. The page passes `agents` when the reader arrived at
   * the `?tab=agents` alias, so that link still lands where it used to. After
   * that it is plain local UI state — deliberately NOT a query param, because
   * nothing links to a sub-view and a second addressing scheme under the
   * page-level `?tab=` would be one more thing to keep honest.
   */
  initialSection?: ConfigView;
}

export function ServiceConfigTab({
  collaborationConfigId,
  serviceId,
  endpoint,
  initialSection,
}: ServiceConfigTabProps) {
  const toast = useToast();
  const schedulesEnabled = useFlag('innovate.fe.schedules.manage');
  /**
   * Access has its own lever (task 1216): the auth policy decides who may call
   * the service, the most consequential field on this tab. Off, the Access
   * entry leaves the rail and `?section=access` lands on the graph.
   */
  const accessEnabled = useFlag('innovate.fe.services.access');
  /**
   * The sub-view is addressable as `?section=` (task 973).
   *
   * Task 803 deliberately kept it OUT of the URL — nothing linked to a
   * sub-view then. Something does now: the Interface tab's publishing card and
   * the header's access badge both point at the Access section, and both act
   * on a page that is already mounted, so a prop read once at mount could not
   * carry them. The param is the tab's own; the page only writes it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const [requestedView, setView] = useState<ConfigView>(
    initialSection ?? (isConfigView(sectionParam) ? sectionParam : 'graph')
  );

  // A `?section=` arriving after mount (a link clicked on this very page) moves
  // the view; a rail click that wrote the param re-runs this with the value it
  // just set, which is a no-op.
  useEffect(() => {
    if (isConfigView(sectionParam)) setView(sectionParam);
  }, [sectionParam]);

  // A deep link can name `access` or `schedules` on a host that gave us no
  // endpoint — or `schedules` with the flag off; landing on the graph beats
  // rendering a rail entry that is not there.
  const schedulesAvailable = !!endpoint && schedulesEnabled;
  const accessAvailable = !!endpoint && accessEnabled;
  const unavailableView =
    (requestedView === 'access' && !accessAvailable) ||
    (requestedView === 'schedules' && !schedulesAvailable);
  const view: ConfigView = unavailableView ? 'graph' : requestedView;
  const views = [
    ...VIEWS,
    ...(accessAvailable ? [ACCESS_VIEW] : []),
    ...(schedulesAvailable ? [SCHEDULES_VIEW] : []),
  ];
  const { data: config, isLoading } = useCollaborationConfig(
    collaborationConfigId ?? undefined
  );
  const updateConfig = useUpdateCollaborationConfig();
  const [validation, setValidation] = useState<DefinitionValidationResponse | null>(null);
  /**
   * The form's own mode/dirty state and the three actions that move between
   * them (task 806). Held here because the controls belong in this panel's
   * header, not at the bottom of the form.
   */
  const [formState, setFormState] = useState<ConfigFormViewState | null>(null);

  const counts = useMemo(() => {
    const definition = (config?.definition ?? {}) as {
      nodes?: unknown[];
      edges?: unknown[];
    };
    return {
      nodes: Array.isArray(definition.nodes) ? definition.nodes.length : 0,
      edges: Array.isArray(definition.edges) ? definition.edges.length : 0,
    };
  }, [config]);

  const validate = useMutation({
    mutationFn: () =>
      collaborationConfigValidationApi
        .validate(config!.definition)
        .then(res => res.data.data),
    onSuccess: result => {
      setValidation(result);
      if (result.valid) {
        toast.success('Definition is valid');
      } else {
        toast.error('Definition has validation errors');
      }
    },
    onError: () => toast.error('Validation request failed'),
  });

  /**
   * The section rail, hoisted out of the main return (task 973) so the Access
   * view — which needs neither a loaded collaboration config nor one attached
   * at all — can render the same row above itself.
   *
   * A REAL TAB BAR (task 807). The pill row this replaces did not read as
   * tabs — kv's words — so it uses the page-level tab row's own treatment from
   * `ServiceDetailPage`: an underline active indicator on a bottom-bordered
   * row, the same typography, the same hover and dark-mode colors. It is
   * rendered TIGHTER (px-3 py-1.5 against the page row's px-4 py-2) because it
   * is secondary to those tabs and must never compete with the row directly
   * above it.
   */
  /** Move to a view and say so in the URL, so the sub-view is shareable. */
  const selectView = (id: ConfigView) => {
    setView(id);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('section', id);
        return next;
      },
      { replace: true }
    );
  };

  const sectionRail = (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 dark:border-gray-700">
      <div
        role="tablist"
        aria-label="Config views"
        data-testid="service-config-subnav"
        className="-mb-px flex flex-wrap space-x-1"
      >
        {views.map(({ id, label, testId }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            data-testid={testId}
            onClick={() => selectView(id)}
            className={`px-3 py-1.5 text-sm font-medium focus:outline-none ${
              view === id
                ? 'border-b-2 border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Edit / Save / Cancel, top-right of the panel (task 806). The mode
          spans both form segments — a change made in the schemas view shows
          Save while the graph view is on screen, because they are one form and
          one save. Agents and Access are reading/own-save surfaces with no
          ConfigForm behind them, so the controls step aside there. */}
      {view !== 'agents' && view !== 'access' && view !== 'schedules' && (
        <ConfigModeControls state={formState} className="pb-1.5" />
      )}
    </div>
  );

  /**
   * Access comes FIRST, before the config guards below (task 973). Who may call
   * the service is answerable whether or not a collaboration config is attached
   * or loaded, and a `?section=access` deep link must land on it either way.
   */
  if (view === 'access' && endpoint && accessAvailable) {
    return (
      <div className="space-y-6" data-testid="service-config-access">
        {sectionRail}
        <ServiceAccessSection endpoint={endpoint} />
      </div>
    );
  }

  /**
   * Schedules comes first too, and for the same reason: a schedule runs the AI
   * Service, and whether a collaboration config is attached or loaded changes
   * nothing about listing, pausing or deleting one.
   */
  if (view === 'schedules' && endpoint) {
    return (
      <div className="space-y-6" data-testid="service-config-schedules">
        {sectionRail}
        <ServiceSchedulesSection endpoint={endpoint} />
      </div>
    );
  }

  if (!collaborationConfigId) {
    return (
      <div className="space-y-6">
        {endpoint && sectionRail}
        <div className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          This service has no collaboration config attached.
        </div>
      </div>
    );
  }

  if (isLoading || !config) {
    return (
      <div className="space-y-6">
        {endpoint && sectionRail}
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500 dark:text-gray-400">Loading config…</div>
        </div>
      </div>
    );
  }

  /**
   * The Graph Config section's toolbar: what this config IS (name, node/edge
   * counts, last validation result) and the two things you can do to the
   * definition. It is passed into `ConfigForm` rather than rendered above it
   * so it sits in the section header row — validation is definition
   * validation, so it belongs to the graph section, not to a floating card.
   */
  const graphToolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 font-mono text-xs text-gray-700 dark:text-gray-300">
        {config.name}
      </span>
      <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300">
        {counts.nodes} nodes · {counts.edges} edges
      </span>
      {validation && (
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium ${
            validation.valid
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}
        >
          {validation.valid ? (
            <CheckCircleIcon className="h-4 w-4" />
          ) : (
            <ExclamationTriangleIcon className="h-4 w-4" />
          )}
          {validation.valid
            ? 'Definition valid'
            : `${validation.errors.length} error${validation.errors.length === 1 ? '' : 's'}`}
        </span>
      )}
      <button
        type="button"
        onClick={() => validate.mutate()}
        disabled={validate.isPending}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
      >
        <ShieldCheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
        {validate.isPending ? 'Validating…' : 'Validate'}
      </button>
      <Link
        to={`/configs/${config.id}`}
        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <ArrowTopRightOnSquareIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
        Open in Advanced
      </Link>
    </div>
  );

  /** Server-side validation issues, when present. */
  const graphNotice =
    validation && !validation.valid ? (
      <ul className="mt-3 space-y-1 text-xs text-red-600 dark:text-red-400">
        {validation.errors.map((error, index) => {
          const message =
            typeof error.message === 'string' ? error.message : JSON.stringify(error);
          const nodeId = typeof error.nodeId === 'string' ? error.nodeId : null;
          return (
            <li key={index}>
              {nodeId && <span className="font-mono">{nodeId}: </span>}
              {message}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div className="space-y-6">
      {/* Sub-navigation: one view at a time, sitting directly under the page's
          own tab row. */}
      {sectionRail}

      {/* The same editing surface as Advanced → Collaboration Configs, scoped
          to this service's config. The two form views share one save; the
          hidden one stays mounted, so switching views never costs an edit.
          Saving stays on this page. */}
      <ConfigForm
        initialData={config}
        mode="edit"
        layout="sub-navigated"
        activeSection={
          view === 'agents' || view === 'access' || view === 'schedules' ? 'none' : view
        }
        graphSectionToolbar={graphToolbar}
        graphSectionNotice={graphNotice}
        viewModeEnabled
        onViewStateChange={setFormState}
        onSubmit={async data => {
          await updateConfig.mutateAsync({
            id: config.id,
            data: { ...data, name: data.name || config.name },
          });
          toast.success('Config updated');
          setValidation(null);
        }}
        onCancel={() => {
          // Stay in the service context — without this, ConfigForm navigates
          // to the Advanced /configs list on cancel.
        }}
      />

      {/* The agents this definition names, in full — its own view now (task
          803) rather than a fold beneath the editor. Mounted alongside the
          form and hidden the same way, so its master–detail selection survives
          a trip to the graph and back. */}
      <section
        data-testid="service-config-agents"
        hidden={view !== 'agents'}
        className="space-y-4"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Who does the work when this service is called — every agent the definition
          references.
        </p>
        <ServiceAgentsTab collaborationConfigId={collaborationConfigId} serviceId={serviceId} />
      </section>
    </div>
  );
}
